import { MailerOptions } from '@nestjs-modules/mailer';
import { PaymentMethodEnum } from '../../enum';
import { ServiceConfig } from './service.config';
import { ApplePayConfig } from './apple-pay.config';
import { BoipaConfig } from './boipa.config';
import { SendInBlueConfig } from './send-in-blue.config';
import { FireConfig } from './fire.config';
import { FireOpenPaymentsConfig } from './fire-open-payments.config';
import { FireAccountsConfig } from './fire-accounts.config';
import { FirePaymentsConfig } from './fire-payments.config';
import { BrightLocalConfig } from './bright-local.config';
import { PurchaseOrderInvoiceConfig } from './purchase-order-invoice.config';
import { GoogleConfig } from './google.config';
import { QueueConfig } from './queue.config';
import { PeopleRosterConfig } from './people-roster.config';
import { CacheConfig } from './cache.config';
import { GetStreamConfig } from './get-stream.config';
import { StripeConfig } from './stripe.config';
import { SurfboardConfig } from "./surfboard.config";
import { SweetFaceConfig } from './sweet-face.config';
import { VectronIntegrationConfig } from './vectron-integration.config';
import { GoCardlessConfig } from './go-cardless.config';
import { AisConfig } from './ais.config';
import { GoogleWalletConfig } from './google-wallet.config';
import { AppleWalletConfig } from './apple-wallet.config';
import { EdiConfirmationConfig } from './edi-confirmation.config';
import { AutomaticProcessingConfig } from './automatic-processing.config';
import { SynergyConfig } from './synergy.config';
import { AibConfig } from './aib.config';

export const API_CONFIG = 'API_CONFIG';

export interface APIConfig {
  production: boolean;
  versionDataFile: string;
  API_PREFIX: string;
  PORT: number;
  DB_TYPE: string;
  DB_HOST: string;
  DB_PORT: number;
  DB_USERNAME: string;
  DB_PASSWORD: string;
  DB_NAME: string;
  DB_SYNCHRONISE: boolean;
  DB_MIGRATIONS_RUN: boolean;
  DB_MIGRATIONS: string[];
  DB_CLI_MIGRATIONS_DIRECTORY: string;
  DB_LOGGING: boolean;
  DB_LOGGER_TYPE: string;
  DB_AUT_LOAD_ENTITIES: boolean;
  DB_COLLATION: string;
  AWS: {
    S3: {
      ACCESS_KEY_ID: string;
      SECRET_ACCESS_KEY: string;
      REGION: string;
      IMAGES_BUCKET_NAME: string;
      DOCUMENTS_BUCKET_NAME: string;
      CACHE_CONTROL: string;
    };
  };
  DB_USE_SSL: boolean;
  READONLY_DB_TYPE: string;
  READONLY_DB_HOST: string;
  READONLY_DB_PORT: number;
  READONLY_DB_USERNAME: string;
  READONLY_DB_PASSWORD: string;
  READONLY_DB_NAME: string;
  READONLY_DB_SYNCHRONISE: boolean;
  READONLY_DB_MIGRATIONS_RUN: boolean;
  READONLY_DB_MIGRATIONS: string[];
  READONLY_DB_CLI_MIGRATIONS_DIRECTORY: string;
  READONLY_DB_LOGGING: boolean;
  READONLY_DB_LOGGER_TYPE: string;
  READONLY_DB_AUT_LOAD_ENTITIES: boolean;
  READONLY_DB_USE_SSL: boolean;
  firebaseConfig: {
    apiKey: string;
    authDomain: string;
    databaseURL: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId: string;
    clientEmail: string;
    privateKey: string;
  };
  jwtConfig: {
    accessTokenConfig: {
      expiresIn: number; // seconds
      issuer: string;
      privateKey: string;
      publicKey: string;
    };
    refreshTokenConfig: {
      expiresIn: number; //seconds
      issuer: string;
      privateKey: string;
      publicKey: string;
    };
  };
  mailerOptions: MailerOptions;
  boipaConfig: BoipaConfig;
  aibConfig: AibConfig;
  sendinblue: SendInBlueConfig;
  brightlocal: BrightLocalConfig;
  fire: FireConfig;
  firePayments: FirePaymentsConfig
  fireAccounts: FireAccountsConfig;
  fireOpenPayments: FireOpenPaymentsConfig;
  flightInfo?: ServiceConfig;
  forecaster: ServiceConfig;
  reportingUpco: ServiceConfig;
  reviewTrackers: ServiceConfig;
  pieCloudSignup: ServiceConfig;
  crons: {
    //controls both PRQ processing and cleanup task
    printing: boolean;
    aclShareTask: boolean;
    brightlocalFetchReviews: boolean;
    giftCardDeliveryDateEmail: boolean;
    orderScheduledToNew: boolean;
    merchantStockUsage: boolean;
    purchaseOrdersToBePlaced: boolean;
    treasuryCreateTransferBatches: boolean;
    treasuryArchiveCompleteBatches: boolean;
    purchasePausedCheck?: boolean;
    treasuryAmendBatchStatuses: boolean;
    treasuryRemoveEphemeralRecords: boolean;
    cleanupExpiredStocks: boolean;
    populateStockMerchantPurchasingDetails: boolean;
    generateBillingInvoices: boolean;
    onboardingCleanup: boolean;
    /** Enable Supplier Stock sync process. */
    supplierStockSync: boolean;
    employeeBenefitTopUp: boolean;
    /** Enable EDI Confirmation file fetch process. */
    ediConfirmationFetch: boolean;
    /** Enable Purchase Order Invoice file fetch process. */
    purchaseOrderInvoiceFetch: boolean;
    clearLocalStorageExpiredItems: boolean;
    /** Fire processes */
    updateFireBalancesForBusinessEntities: boolean;
    updateFireTransactionsForBusinessEntities: boolean;
    /** Stripe - Validate Payment Breakdown Fees */
    checkToUpdatePaymentBreakdownFeeStatus: boolean;
    /** AIS processes */
    aisInstitutionsUpsert: boolean;
    aisUpdateAllTransactions: boolean;
    aisValidateAllRequisitions: boolean;
    /** automatic processing of physical gift cards */
    automaticPhysicalCardsProcessing: boolean; // change to true to enable
    /** Enable automatic sweet face card transactions cash up */
    automaticSweetFaceCashUp: boolean;
    downstreamSchedule?: boolean;
    upstreamSchedule?: boolean;
  };
  recaptchaSecretKey: string;
  google: GoogleConfig;
  googleWallet?: GoogleWalletConfig;
  appleWallet?: AppleWalletConfig;
  apiUrl: string;
  pieCloudUrl: string;
  GiftCard_URL: string;
  sha256HMACKey: string;
  THROTTLE_TTL: number; // Time to live of request for throttling IP address.
  THROTTLE_LIMIT: number; // Limit of request for THROTTLE_TTL.
  LOGIN_TTL: number; // Time to live in seconds, for login tracker.
  LOGIN_FAILED_ATTEMPTS_LIMIT: number; //  How many failed login attempts until locking an account.
  ACCOUNT_LOCK_TIME_MINUTES: number; // Time in minutes of account lock when to many failed login attempts.
  piecloud_Invoicing: {
    API: string;
  };
  invoiceCollectConfig: {
    apiUrl: string;
  };
  publickApiGiftCardUrl: string;
  //local api proxy client - used when calling the local api remote proxy from another api instance
  localAPIProxyClient?: {
    nodeID: string;
    authCredential: string;
    apiProxyBaseURL: string;
    enabled: boolean;
  };
  //local api proxy server - used when calling the local api remote proxy directly
  localAPIProxy?: {
    nodeID: string;
    defaultGateway: string; //used to check if the local api network can be reached
    localInstancePort: number;
    localInstanceProtocol: string;
    remoteProxyAuthType: string;
    keys: //TODO: implement key repo in db / kms
    {
      origin: string;
      key: string;
      validUntil: number;
    }[];
    enabled: boolean;
  };
  applePayConfig: ApplePayConfig | ApplePayConfig[];
  peopleRoster: PeopleRosterConfig;
  billing: {
    subscription: {
      invoiceMonthlyDay: number;
      invoiceWeeklyDay: number;
      invoiceFrom: string;
      invoiceDueDays: number;
      invoiceToEmail: string;
      vatRate: number;
      defaultNoDaysCicle: number;
      initialPaymentMethods: PaymentMethodEnum[];
    };
  };
  invoicing: {
    entityName: string;
    entityAddress: string;
    entityVatNumber: string;
    entityEmail: string;
  };
  webPayment: {
    url: string;
  };
  azureStorageAccountConnection: string;
  supplierStockSync: {
    logContainerName: string;
    musgrave: {
      authorisation: {
        tokenHost: string;
        tokenPath: string;
        grantType: string;
        clientId: string;
        clientSecret: string;
        scope: string;
      };
      sync: {
        messageQueueName: string;
        clientId: string;
        syncUrl: string;
        /** global_supplier table IDs */
        syncGlobalSupplierIds: number[];
      };
    };
  };
  cacheConfig: CacheConfig;
  queueConfig: QueueConfig;
  getStream: GetStreamConfig;
  stripe: StripeConfig;
  surfboard: SurfboardConfig;
  ediConfirmation: EdiConfirmationConfig;
  purchaseOrderInvoice: PurchaseOrderInvoiceConfig;
  sweetFace: SweetFaceConfig;
  vectronIntegration: VectronIntegrationConfig;
  goCardless: GoCardlessConfig;
  ais: AisConfig;
  automaticPhysicalCardsProcessing: AutomaticProcessingConfig;
  // Local sync related
  remoteURLBase?: string;
  synergy?: SynergyConfig;
  azureSpeech: {
    region: string;
    endpoint: string,
    key: string
  },
  privacyPolicy: {
    giftCards: {
      merchantWebshopURL: string;
      termsAndConditionsURL: string;
      privacyPolicyURL: string;
    }
  }
}